import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, Eye, EyeOff, LogIn, UserPlus, Shield, KeyRound } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";

import { useToast } from "@/hooks/use-toast";
import { useCaregiverTranslation } from "@/hooks/useCaregiverTranslation";
import { Globe } from "lucide-react";
import { z } from "zod";
import appLogo from "@/assets/eyecontrol-icon-white.png";

const authSchema = (t: any) => z.object({
  email: z.string().email(t("invalidEmail")),
  password: z.string().min(6, t("passwordMinLength"))
});

const resetSchema = (t: any) => z.object({
  email: z.string().email(t("invalidEmail"))
});

export default function Auth() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t, dir, isRTL, lang, setLanguage } = useCaregiverTranslation();
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{email?: string;password?: string;}>({});
  const [needsMfa, setNeedsMfa] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaFactorId, setMfaFactorId] = useState("");
  const [mfaChallengeId, setMfaChallengeId] = useState("");
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [isResetting, setIsResetting] = useState(false);
  const [showNewPasswordForm, setShowNewPasswordForm] = useState(false);
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [signupEmailSent, setSignupEmailSent] = useState(false);

  // Responsive sizing to avoid internal scrolling/cropping on shorter laptop viewports
  const controlHeight = "clamp(2.5rem, 6vh, 3rem)";
  const cardPadding = "clamp(1rem, 3vh, 1.5rem)";
  const logoBoxSize = "clamp(3rem, 9vh, 5rem)";
  const logoIconSize = "clamp(2.25rem, 6.5vh, 3.5rem)";
  const titleFontSize = "clamp(1.375rem, 3.6vh, 1.875rem)";
  const subtitleFontSize = "clamp(0.875rem, 2.2vh, 1rem)";
  const sectionGap = "clamp(0.625rem, 1.8vh, 1rem)";
  const dividerMargin = "clamp(0.5rem, 1.8vh, 1rem)";

  useEffect(() => {
    // Detect Supabase password recovery callback in URL hash
    const hash = window.location.hash || "";
    const isRecoveryCallback = hash.includes("type=recovery");
    if (isRecoveryCallback) {
      setShowNewPasswordForm(true);
      // If a different (e.g. Google OAuth) session is active in the background,
      // it can interfere with applying the recovery token. Sign out first so
      // the recovery flow installs a clean session for updateUser().
      supabase.auth.getSession().then(({ data }) => {
        const provider = data.session?.user?.app_metadata?.provider;
        if (provider && provider !== 'email') {
          supabase.auth.signOut();
        }
      });
    }

    // Check for OAuth/auth error in URL
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const error = hashParams.get('error');
    const errorDescription = hashParams.get('error_description');

    if (error) {
      toast({
        title: t("authError"),
        description: errorDescription || t("authError"),
        variant: "destructive"
      });
      window.history.replaceState(null, '', window.location.pathname);
      return;
    }

    // Listen for auth changes FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Password recovery: keep user on this page to set a new password
      if (event === "PASSWORD_RECOVERY") {
        setShowNewPasswordForm(true);
        return;
      }

      if (event === "SIGNED_IN" && session) {
        // If this sign-in came from a recovery link, do not navigate away;
        // the user must set a new password first.
        if (isRecoveryCallback || showNewPasswordForm) {
          setShowNewPasswordForm(true);
          return;
        }

        const provider = session.user.app_metadata?.provider;
        const isOAuthUser = provider && provider !== 'email';

        setTimeout(async () => {
          if (isOAuthUser) {
            const { data: mfaData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
            if (mfaData?.currentLevel === "aal1" && mfaData?.nextLevel === "aal2") {
              setNeedsMfa(true);
              await startMfaChallenge();
            } else {
              navigate("/");
            }
            return;
          }

          const isEmailConfirmed = Boolean(
            session.user.email_confirmed_at || (session.user as any).confirmed_at
          );

          if (!isEmailConfirmed) {
            await supabase.auth.signOut();
            return;
          }

          const { data: mfaData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
          if (mfaData?.currentLevel === "aal1" && mfaData?.nextLevel === "aal2") {
            setNeedsMfa(true);
            await startMfaChallenge();
          } else {
            navigate("/");
          }
        }, 0);
      }
    });

    // THEN check for existing session
    const checkSession = async () => {
      // Skip auto-navigation when handling a password recovery callback
      if (isRecoveryCallback) return;
      const { data: { session } } = await supabase.auth.getSession();

      if (session) {
        const provider = session.user.app_metadata?.provider;
        const isOAuthUser = provider && provider !== 'email';

        // OAuth users can proceed without our custom verification
        if (isOAuthUser) {
          const { data: mfaData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
          if (mfaData?.currentLevel === "aal1" && mfaData?.nextLevel === "aal2") {
            setNeedsMfa(true);
            await startMfaChallenge();
          } else {
            navigate("/");
          }
          return;
        }

        // For email users, treat platform-confirmed email as verified (fallback)
        const isEmailConfirmed = Boolean(
          session.user.email_confirmed_at || (session.user as any).confirmed_at
        );

        if (!isEmailConfirmed) {
          await supabase.auth.signOut();
          return;
        }

        // Check if MFA is required
        const { data: mfaData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        if (mfaData?.currentLevel === "aal1" && mfaData?.nextLevel === "aal2") {
          setNeedsMfa(true);
          await startMfaChallenge();
        } else {
          navigate("/");
        }
      }
    };
    checkSession();

    return () => subscription.unsubscribe();
  }, [navigate, toast]);

  const startMfaChallenge = async () => {
    try {
      const { data: factors } = await supabase.auth.mfa.listFactors();
      const totpFactor = factors?.totp.find((f) => f.status === "verified");

      if (totpFactor) {
        setMfaFactorId(totpFactor.id);
        const { data: challenge, error } = await supabase.auth.mfa.challenge({
          factorId: totpFactor.id
        });

        if (error) throw error;
        setMfaChallengeId(challenge.id);
      }
    } catch (error) {







      // MFA challenge error handled silently
    }
  };

  const handleResetPassword = async () => {
    try {
      resetSchema(t).parse({ email: resetEmail });
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: t("authError"),
          description: error.errors[0].message,
          variant: "destructive"
        });
      }
      return;
    }

    setIsResetting(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/auth`,
      });

      if (error) throw error;

      toast({
        title: t("resetEmailSent"),
        description: t("resetEmailDesc")
      });
      setShowResetPassword(false);
      setResetEmail("");
    } catch (error: any) {
      toast({
        title: t("authError"),
        description: t("resetError"),
        variant: "destructive"
      });
    } finally {
      setIsResetting(false);
    }
  };

  const handleNewPassword = async () => {
    if (newPassword.length < 6) {
      toast({
        title: t("authError"),
        description: t("passwordMinLength"),
        variant: "destructive"
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({
        title: t("authError"),
        description: t("passwordsDoNotMatch"),
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;

      setShowNewPasswordForm(false);
      setResetToken("");
      window.history.replaceState(null, '', '/auth');
      setNewPassword("");
      setConfirmPassword("");

      toast({
        title: t("passwordResetSuccess"),
        description: t("passwordResetSuccessDesc")
      });
      // User is already signed in via the recovery session; route to home
      navigate("/");
    } catch (error: any) {
      const errMsg = error?.message || "";
      let description = errMsg || t("resetError");
      if (errMsg.includes("leaked") || errMsg.includes("pwned") || errMsg.includes("breached") || errMsg.includes("HIBP") || error?.code === "weak_password") {
        description = t("passwordBreached");
      } else if (errMsg.includes("Auth session missing") || errMsg.includes("session_not_found") || errMsg.includes("expired")) {
        description = t("invalidResetLink");
      } else if (errMsg.includes("same") && errMsg.includes("password")) {
        description = errMsg;
      }
      toast({
        title: t("authError"),
        description,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };


  const verifyMfa = async () => {
    if (mfaCode.length !== 6) {
      toast({
        title: t("authError"),
        description: t("mfaCodeError"),
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.mfa.verify({
        factorId: mfaFactorId,
        challengeId: mfaChallengeId,
        code: mfaCode
      });

      if (error) throw error;

      toast({
        title: t("loginSuccess")
      });
      navigate("/");
    } catch (error: any) {
      toast({
        title: t("authError"),
        description: t("mfaCodeWrong"),
        variant: "destructive"
      });
      // Start a new challenge
      await startMfaChallenge();
      setMfaCode("");
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    try {
      authSchema(t).parse({ email, password });
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: {email?: string;password?: string;} = {};
        error.errors.forEach((err) => {
          if (err.path[0] === "email") fieldErrors.email = err.message;
          if (err.path[0] === "password") fieldErrors.password = err.message;
        });
        setErrors(fieldErrors);
      }
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setSignupEmailSent(false);
    setLoading(true);

    try {
      // Clear any existing session (e.g. stale Google OAuth from another tab)
      // before attempting a new login. This prevents account mixing where
      // a background Gmail session overrides the email/password credentials.
      await supabase.auth.signOut();

      if (isLogin) {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password
        });
        if (error) throw error;

        // Check if user has verified their email (fallback: platform-confirmed counts as verified)
        const signedInUser = data.session?.user;
        const isEmailConfirmed = Boolean(
          signedInUser?.email_confirmed_at || (signedInUser as any)?.confirmed_at
        );

        if (!isEmailConfirmed) {
          await supabase.auth.signOut();
          toast({
            title: t('authError'),
            description: t('emailNotConfirmed'),
            variant: 'destructive'
          });
          setLoading(false);
          return;
        }

        // Check if MFA is required
        const { data: mfaData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        if (mfaData?.currentLevel === "aal1" && mfaData?.nextLevel === "aal2") {
          setNeedsMfa(true);
          await startMfaChallenge();
          return;
        }

        toast({
          title: t("loginSuccess")
        });
      } else {
        // Sign up user (won't be confirmed yet)
        const { data: signUpData, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/`
          }
        });
        if (error) throw error;

        // Supabase returns a user with an empty `identities` array when the
        // email is already registered (anti-enumeration behavior). Detect
        // this and surface a clear message instead of a misleading success.
        if (signUpData?.user && (signUpData.user.identities?.length ?? 0) === 0) {
          toast({
            title: t("authError"),
            description: t("userExists"),
            variant: "destructive",
          });
          setLoading(false);
          return;
        }

        // Supabase sends its own confirmation email on signUp().
        // We no longer send a second one via custom-auth-email to avoid
        // the user receiving duplicate verification emails.

        setSignupEmailSent(true);
        toast({
          title: t("signupSuccess"),
          description: t("checkEmail")
        });
        return; // Don't redirect, wait for email verification
      }
    } catch (error: any) {
      let message = t("authError");
      const errMsg = error.message || "";
      if (errMsg.includes("Invalid login credentials")) {
        message = t("invalidCredentials");
      } else if (errMsg.includes("User already registered")) {
        message = t("userExists");
      } else if (errMsg.includes("Email not confirmed")) {
        message = t("emailNotConfirmed");
      } else if (errMsg.includes("leaked") || errMsg.includes("pwned") || errMsg.includes("breached") || errMsg.includes("HIBP") || (error.code === "weak_password" && errMsg.includes("password"))) {
        message = t("passwordBreached");
      } else if (errMsg.includes("rate limit") || errMsg.includes("Email rate limit") || error.code === "over_email_send_rate_limit") {
        message = errMsg;
      } else if (errMsg) {
        // Surface the underlying message instead of a generic error so issues
        // like SMTP failures, rate limits, or invalid email are visible.
        message = errMsg;
      }
      toast({
        title: t("authError"),
        description: message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // MFA Verification Screen
  if (needsMfa) {
    return (
      <div className="h-[100dvh] w-full bg-background flex justify-center overflow-y-auto p-4" dir={dir}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md my-auto">
          
          <div className="bg-card border border-border rounded-3xl p-8 shadow-xl">
            <div className="text-center mb-8">
              <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-primary/10 flex items-center justify-center">
                <Shield className="w-10 h-10 text-primary" />
              </div>
              <h1 className="text-2xl font-bold text-foreground">{t("twoFactorAuth")}</h1>
              <p className="text-muted-foreground mt-2">
                {t("enterAuthCode")}
              </p>
            </div>

            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="mfaCode">{t("verificationCode")}</Label>
                <Input
                  id="mfaCode"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000"
                  className="text-center text-2xl tracking-widest font-mono h-14"
                  dir="ltr"
                  maxLength={6}
                  autoFocus />
                
              </div>

              <Button
                onClick={verifyMfa}
                disabled={loading || mfaCode.length !== 6}
                className="w-full h-12 text-lg font-medium">
                
                {loading ?
                <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" /> :

                t("verifyAndLogin")
                }
              </Button>

              <Button
                variant="ghost"
                onClick={async () => {
                  await supabase.auth.signOut();
                  setNeedsMfa(false);
                  setMfaCode("");
                }}
                className="w-full">
                
                {t("backToLogin")}
              </Button>
            </div>
          </div>
        </motion.div>
      </div>);

  }

  // New Password Form (after clicking reset link)
  if (showNewPasswordForm) {
    return (
      <div className="h-[100dvh] w-full bg-background flex justify-center overflow-y-auto p-4" dir={dir}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md my-auto">
          
          <div className="bg-card border border-border rounded-3xl p-8 shadow-xl">
            <div className="text-center mb-8">
              <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-primary/10 flex items-center justify-center">
                <KeyRound className="w-10 h-10 text-primary" />
              </div>
              <h1 className="text-2xl font-bold text-foreground">{t("setNewPassword")}</h1>
              <p className="text-muted-foreground mt-2">
                {t("resetEmailDesc")}
              </p>
            </div>

            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="newPassword">{t("newPassword")}</Label>
                <div className="relative">
                  <Lock className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground ${isRTL ? 'right-3' : 'left-3'}`} />
                  <Input
                    id="newPassword"
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••••"
                    className={`h-12 ${isRTL ? 'pr-10 pl-10' : 'pl-10 pr-10'}`}
                    dir="ltr" />
                  
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground ${isRTL ? 'left-3' : 'right-3'}`}>
                    
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{t("confirmNewPassword")}</Label>
                <div className="relative">
                  <Lock className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground ${isRTL ? 'right-3' : 'left-3'}`} />
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    className={`h-12 ${isRTL ? 'pr-10' : 'pl-10'}`}
                    dir="ltr" />
                  
                </div>
              </div>

              <Button
                onClick={handleNewPassword}
                disabled={loading || !newPassword || !confirmPassword}
                className="w-full h-12 text-lg font-medium">
                
                {loading ?
                <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" /> :

                t("setNewPassword")
                }
              </Button>

              <Button
                variant="ghost"
                onClick={() => {
                  setShowNewPasswordForm(false);
                  setResetToken("");
                  setNewPassword("");
                  setConfirmPassword("");
                  window.history.replaceState(null, '', '/auth');
                }}
                className="w-full">
                
                {t("backToLogin")}
              </Button>
            </div>
          </div>
        </motion.div>
      </div>);

  }

  return (
    <div className="h-[100dvh] w-full bg-background flex justify-center overflow-y-auto p-2 sm:p-4" dir={dir}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md my-auto">
        
        <div
          className="bg-card border border-border rounded-3xl shadow-xl"
          style={{ padding: cardPadding }}>
          
          {/* Logo */}
          <div className="text-center" style={{ marginBottom: sectionGap }}>
            <div
              className="mx-auto mb-2 sm:mb-4 rounded-2xl bg-primary flex items-center justify-center"
              style={{ width: logoBoxSize, height: logoBoxSize }}>
              
              <img
                src={appLogo}
                alt="EyeControl App"
                style={{ width: logoIconSize, height: logoIconSize }} />
              
            </div>
            <h1
              className="font-bold text-foreground"
              style={{ fontSize: titleFontSize }}>
              
              EyeControl App 
            </h1>
            <p
              className="text-muted-foreground"
              style={{ marginTop: "clamp(0.25rem, 1vh, 0.5rem)", fontSize: subtitleFontSize }}>
              
              {isLogin ? t("loginToAccount") : t("createAccount")}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex flex-col" style={{ gap: sectionGap }}>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-foreground">
                {t("emailLabel")}
              </Label>
              <div className="relative">
                <Mail className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground ${isRTL ? 'right-3' : 'left-3'}`} />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setSignupEmailSent(false); }}
                  placeholder="email@example.com"
                  className={`${isRTL ? 'pr-10' : 'pl-10'}`}
                  style={{ height: controlHeight }}
                  dir="ltr"
                  autoComplete="email" />
                
              </div>
              {errors.email &&
              <p className="text-sm text-destructive">{errors.email}</p>
              }
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-foreground">
                {t("passwordLabel")}
              </Label>
              <div className="relative">
                <Lock className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground ${isRTL ? 'right-3' : 'left-3'}`} />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setSignupEmailSent(false); }}
                  placeholder="••••••••"
                  className={`${isRTL ? 'pr-10 pl-10' : 'pl-10 pr-10'}`}
                  style={{ height: controlHeight }}
                  dir="ltr"
                  autoComplete={isLogin ? "current-password" : "new-password"} />
                
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={`absolute top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground ${isRTL ? 'left-3' : 'right-3'}`}>
                  
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.password &&
              <p className="text-sm text-destructive">{errors.password}</p>
              }
              {!isLogin && !errors.password &&
              <p className="text-xs text-muted-foreground leading-snug">{t("passwordRequirements")}</p>
              }
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full text-base sm:text-lg font-medium"
              style={{ height: controlHeight }}>
              
              {loading ?
              <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" /> :

              <>
                  {isLogin ? t("login") :
                <>
                      <UserPlus className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                      {t("signup")}
                    </>
                }
                </>
              }
            </Button>
          </form>

          {/* Divider */}

          {/* Toggle */}
          <div className="text-center" style={{ marginTop: sectionGap }}>
            <button
              onClick={() => {
                setIsLogin(!isLogin);
                setErrors({});
                setSignupEmailSent(false);
              }}
              className="text-primary hover:underline font-medium text-sm sm:text-base">
              
              {isLogin ? `${t("noAccount")} ${t("signup")}` : `${t("haveAccount")} ${t("login")}`}
            </button>
          </div>

          {isLogin &&
          <button
            onClick={() => setShowResetPassword(true)}
            className="block w-full text-center text-xs sm:text-sm text-muted-foreground hover:text-primary"
            style={{ marginTop: "clamp(0.25rem, 1.2vh, 0.75rem)" }}>
            
              {t("forgotPassword")}
            </button>
          }

          {!isLogin && signupEmailSent &&
          <p className="mt-2 sm:mt-3 text-center text-xs sm:text-sm text-muted-foreground">
              {t("checkEmail")}
            </p>
          }

          {/* Links */}
          <div className="flex justify-center gap-4 text-xs sm:text-sm" style={{ marginTop: sectionGap }}>
            <a
              href="/legal/privacy"
              className="text-muted-foreground hover:text-primary hover:underline">
              
              {t("privacyPolicy")}
            </a>
            <a
              href="/guide"
              className="text-muted-foreground hover:text-primary hover:underline">
              
              {t("aboutApp")}

            </a>
          </div>

          {/* Language Toggle */}
          <div className="flex justify-center" style={{ marginTop: sectionGap }}>
            <button
              onClick={() => setLanguage(lang === "he" ? "en" : "he")}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-card/80 backdrop-blur-sm border border-border hover:bg-card transition-colors text-sm font-medium text-muted-foreground"
            >
              <Globe className="w-4 h-4" />
              <span>{lang === "he" ? "English" : "עברית"}</span>
            </button>
          </div>

        </div>

        {/* Reset Password Modal */}
        {showResetPassword && <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
          onClick={() => setShowResetPassword(false)}>
          
            <motion.div
            initial={{ scale: 0.95 }}
            animate={{ scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-card border border-border rounded-2xl p-6 w-full max-w-sm">
            
              <h2 className="text-xl font-bold text-foreground mb-2">{t("resetPassword")}</h2>
              <p className="text-muted-foreground text-sm mb-4">
                {t("resetEmailDesc")}
              </p>
              
              <div className="space-y-4">
                <div className="relative">
                  <Mail className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground ${isRTL ? 'right-3' : 'left-3'}`} />
                  <Input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="email@example.com"
                  className={`${isRTL ? 'pr-10' : 'pl-10'}`}
                  style={{ height: controlHeight }}
                  dir="ltr" />
                
                </div>

                <div className="flex gap-2">
                  <Button
                  onClick={handleResetPassword}
                  disabled={isResetting || !resetEmail}
                  className="flex-1">
                  
                    {isResetting ?
                  <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" /> :

                  t("sendResetEmail")
                  }
                  </Button>
                  <Button
                  variant="outline"
                  onClick={() => setShowResetPassword(false)}>
                  
                    {t("cancel")}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        }
      </motion.div>
    </div>);

}